import React, { InputHTMLAttributes, forwardRef } from 'react'
import clsx from 'clsx'

interface CheckboxProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string | JSX.Element
  error?: string
  // react-hook-form register() result, spread onto the input
  register?: any
}

// The input stays in the accessibility tree (sr-only, not display:none), so it
// is reached with Tab and toggled with Space. A 22px box, filled with the
// brand colour and a white check when on, outlined while it has keyboard focus
// (design system "Вибір"); the empty box keeps a 3:1 border. A short text
// label reads 15px bold, a composed one (a consent with links) stays regular.
const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ label, error, register, className, ...inputProps }, ref) => {
    return (
      <>
        <label
          className={clsx(
            'relative inline-flex items-center gap-2.5 min-h-11 cursor-pointer',
            inputProps.disabled && 'pointer-events-none opacity-50',
            className,
          )}
        >
          <input ref={ref} className="sr-only peer" type="checkbox" {...inputProps} {...register} />
          <span
            aria-hidden="true"
            className="flex items-center justify-center w-[1.375rem] h-[1.375rem] shrink-0 rounded-md border-2 border-solid border-control bg-surface text-white transition-colors duration-150 peer-checked:bg-brand-strong peer-checked:border-brand-strong peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand [&>svg]:opacity-0 peer-checked:[&>svg]:opacity-100"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="3.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12l5 5 9-10" />
            </svg>
          </span>
          <span
            className={clsx(
              'text-[0.9375rem] leading-5 text-ink select-none',
              typeof label === 'string' && 'font-bold',
            )}
          >
            {label}
          </span>
        </label>
        {error && <p className="m-0 text-[0.8125rem] font-bold text-danger">{error}</p>}
      </>
    )
  },
)

Checkbox.displayName = 'Checkbox'

export default Checkbox
